import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Upload, CheckCircle, ArrowRight, Plus, Package, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/useToast'
import { tasksApi, kaTemplatesApi } from '@/lib/api'
import { codeStructuresApi } from '@/lib/api/codeStructures'
import { extractApiError, formatValidationError } from '@/lib/api'
import type { KATemplate } from '@/lib/api'
import type { CreateTaskResult, TaskCreationMode } from '@/types'
import { FileImportInline, type FileImportInlineData } from '@/components/FileImportInline'
import {
  collectManualAIs,
  collectAutoAIs,
  aiInputHint,
  autoSourceLabel,
} from '@/lib/ka-template-fields'

function unwrapTemplates(res: unknown): KATemplate[] {
  if (Array.isArray(res)) return res
  const wrapped = res as { data?: KATemplate[] }
  return wrapped?.data ?? []
}

const CREATION_MODE_OPTIONS: { value: TaskCreationMode; label: string; description: string }[] = [
  { value: 'km_and_ka_file', label: 'КМ + КА из файлов', description: 'Загрузка файла КМ и генерация/загрузка КА коробок и паллет' },
  { value: 'ka_file_only', label: 'Только КА из файлов', description: 'КМ сканируются на линии, КА генерируются или загружаются' },
  { value: 'km_file_only', label: 'Только КМ из файла', description: 'Загрузка файла КМ без коробок и паллет' },
]

export function TaskCreatePage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [creationMode, setCreationMode] = useState<TaskCreationMode>('km_and_ka_file')
  const [itemsPerBox, setItemsPerBox] = useState('20')
  const [boxesPerPallet, setBoxesPerPallet] = useState('40')
  const [manualAiValues, setManualAiValues] = useState<Record<string, string>>({})
  const [kaBoxTemplateId, setKaBoxTemplateId] = useState('')
  const [kaPalletTemplateId, setKaPalletTemplateId] = useState('')
  const [kaSourceMode, setKaSourceMode] = useState<'generate' | 'load'>('generate')
  const [kaFileData, setKaFileData] = useState<FileImportInlineData | null>(null)
  const [palletCount, setPalletCount] = useState('3')
  const [productGroupCode, setProductGroupCode] = useState('')
  const [result, setResult] = useState<CreateTaskResult | null>(null)
  const [dupError, setDupError] = useState<{ count: number; url: string } | null>(null)

  const isKmOnly = creationMode === 'km_file_only'
  const requiresKmFile = creationMode === 'km_and_ka_file' || isKmOnly
  const hasPalletLevel = !isKmOnly

  const { data: productGroupsResp } = useQuery({
    queryKey: ['product-groups'],
    queryFn: async () => (await codeStructuresApi.list()).data,
    staleTime: 60_000,
  })
  const productGroups = productGroupsResp?.items ?? []

  const { data: boxTemplatesResp } = useQuery({
    queryKey: ['ka-templates', 'ka_box'],
    queryFn: async () => (await kaTemplatesApi.getByType('ka_box')).data,
    staleTime: 60_000,
  })
  const { data: palletTemplatesResp } = useQuery({
    queryKey: ['ka-templates', 'ka_pallet'],
    queryFn: async () => (await kaTemplatesApi.getByType('ka_pallet')).data,
    staleTime: 60_000,
  })

  const boxTemplates = unwrapTemplates(boxTemplatesResp)
  const palletTemplates = unwrapTemplates(palletTemplatesResp)

  useEffect(() => {
    if (boxTemplates.length > 0 && !kaBoxTemplateId) {
      const def = boxTemplates.find(t => t.is_default && t.is_active) ?? boxTemplates.find(t => t.is_active)
      if (def) setKaBoxTemplateId(def.template_id)
    }
  }, [boxTemplates, kaBoxTemplateId])

  useEffect(() => {
    if (palletTemplates.length > 0 && !kaPalletTemplateId) {
      const def = palletTemplates.find(t => t.is_default && t.is_active) ?? palletTemplates.find(t => t.is_active)
      if (def) setKaPalletTemplateId(def.template_id)
    }
  }, [palletTemplates, kaPalletTemplateId])

  const showKaGenerate = kaSourceMode === 'generate'

  const selectedBox = showKaGenerate ? boxTemplates.find(t => t.template_id === kaBoxTemplateId) ?? null : null
  const selectedPallet = showKaGenerate && hasPalletLevel
    ? palletTemplates.find(t => t.template_id === kaPalletTemplateId) ?? null
    : null

  const manualAIFields = useMemo(
    () => collectManualAIs([selectedBox, selectedPallet]),
    [selectedBox, selectedPallet],
  )
  const autoAIFields = useMemo(
    () => collectAutoAIs([selectedBox, selectedPallet]),
    [selectedBox, selectedPallet],
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!productGroupCode) {
      toast.error('Выберите товарную группу')
      return
    }

    if (!isKmOnly) {
      if (showKaGenerate) {
        const missing: string[] = []
        for (const entry of manualAIFields) {
          if (!entry.required) continue
          const val = (manualAiValues[entry.ai] ?? entry.default_value ?? '').trim()
          if (!val) missing.push(`(${entry.ai}) ${entry.label}`)
        }
        if (missing.length > 0) {
          toast.error(`Шаблон КА требует заполнить: ${missing.join(', ')}`)
          return
        }
      }

      if (kaSourceMode === 'load' && !kaFileData) {
        toast.error('Подтвердите файл КА кодов: в секции КА нажмите «Подтвердить выбор»')
        return
      }
    }

    if (requiresKmFile) {
      const codesFile = fileRef.current?.files?.[0]
      if (!codesFile) {
        toast.error('Выберите файл с кодами маркировки')
        return
      }
    }

    const fd = new FormData()
    fd.append('creation_mode', creationMode)
    fd.append('product_group_code', productGroupCode)

    if (requiresKmFile && fileRef.current?.files?.[0]) {
      fd.append('file_km', fileRef.current.files[0])
    }

    if (!isKmOnly) {
      fd.append('items_per_box', itemsPerBox)
      fd.append('boxes_per_pallet', boxesPerPallet)
      fd.append('ka_source', kaSourceMode)
      if (kaSourceMode === 'generate') {
        if (kaBoxTemplateId) fd.append('ka_box_template_id', kaBoxTemplateId)
        if (kaPalletTemplateId) fd.append('ka_pallet_template_id', kaPalletTemplateId)
        if (Object.keys(manualAiValues).length > 0) {
          fd.append('manual_ai_values', JSON.stringify(manualAiValues))
        }
        if (!requiresKmFile) {
          const pallets = parseInt(palletCount) || 0
          const bpp = parseInt(boxesPerPallet) || 1
          fd.append('pallet_count', String(pallets))
          fd.append('box_count', String(pallets * bpp))
        }
      } else if (kaFileData) {
        fd.append('file_ka_boxes', kaFileData.fileBoxes)
        if (kaFileData.filePallets) fd.append('file_ka_pallets', kaFileData.filePallets)
        fd.append('ka_parse_config', JSON.stringify(kaFileData.config))
      }
    }

    setIsSubmitting(true)
    setDupError(null)
    try {
      const { data } = await tasksApi.create(fd)
      setResult(data)
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
      if (errData?.error === 'duplicate_codes' && errData?.download_url) {
        setDupError({
          count: Number(errData.duplicates_count ?? 0),
          url: String(errData.download_url),
        })
        toast.error(`Обнаружено ${errData.duplicates_count ?? 0} дубликатов КМ`)
      } else {
        setDupError(null)
        toast.error(formatValidationError(err) || extractApiError(err) || 'Ошибка создания задания')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCreateAnother = () => {
    setResult(null)
    setDupError(null)
    setKaSourceMode('generate')
    setKaFileData(null)
    setKaBoxTemplateId('')
    setKaPalletTemplateId('')
    setManualAiValues({})
    setPalletCount('3')
    setCreationMode('km_and_ka_file')
    setProductGroupCode('')
    if (fileRef.current) fileRef.current.value = ''
  }

  if (result) {
    const taskId = result.id || result.task_id
    const modeLabel = result.mode === 'ka_file_only'
      ? 'КА из файла / сканирование'
      : result.mode === 'km_file_only'
        ? 'Только КМ'
        : result.mode === 'km_and_ka_file'
          ? 'КМ + КА'
          : String(result.mode)

    return (
      <div className="max-w-2xl">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Задание создано успешно!</h2>

          <div className="bg-gray-50 rounded-lg p-5 mb-6 text-left space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 font-medium">ID задания:</span>
              <span className="font-mono font-bold text-blue-600">{taskId}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 font-medium">Режим:</span>
              <span className="text-sm">{modeLabel}</span>
            </div>
            {result.codes_count > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 font-medium">Кодов маркировки:</span>
                <span className="font-bold text-lg">{result.codes_count}</span>
              </div>
            )}
            {result.mode !== 'km_file_only' && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">Коробок:</span>
                  <span className="font-bold text-lg">{result.boxes_count}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">Палет:</span>
                  <span className="font-bold text-lg">{result.pallets_count}</span>
                </div>
              </>
            )}
          </div>

          {result.parse_warnings?.length > 0 && (
            <details className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
              <summary className="cursor-pointer font-medium text-yellow-800 text-sm">
                Предупреждения при загрузке ({result.parse_warnings.length})
              </summary>
              <ul className="mt-2 space-y-1 text-sm text-yellow-700">
                {result.parse_warnings.map((w, i) => (
                  <li key={i}>• {w}</li>
                ))}
              </ul>
            </details>
          )}

          <div className="flex gap-3 justify-center">
            <Link to={`/admin/tasks/${result.uuid}`}>
              <Button>
                <ArrowRight className="w-4 h-4 mr-1" /> Перейти к заданию
              </Button>
            </Link>
            <Button variant="secondary" onClick={handleCreateAnother}>
              <Plus className="w-4 h-4 mr-1" /> Создать ещё
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Создать задание</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Режим создания</label>
          <select
            value={creationMode}
            onChange={e => setCreationMode(e.target.value as TaskCreationMode)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {CREATION_MODE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {CREATION_MODE_OPTIONS.find(o => o.value === creationMode)?.description}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Товарная группа <span className="text-red-500">*</span>
          </label>
          <select
            value={productGroupCode}
            onChange={e => setProductGroupCode(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">— выберите товарную группу —</option>
            {(() => {
              const enabled = productGroups.filter(g => g.enabled)
              const configured = enabled.filter(g => g.configured)
              const notConfigured = enabled.filter(g => !g.configured)
              return (
                <>
                  {configured.length > 0 && (
                    <optgroup label="Настроенные">
                      {configured.map(g => (
                        <option key={g.code} value={g.code}>{g.name} ({g.code})</option>
                      ))}
                    </optgroup>
                  )}
                  {notConfigured.length > 0 && (
                    <optgroup label="Не настроенные">
                      {notConfigured.map(g => (
                        <option key={g.code} value={g.code}>{g.name} ({g.code}) — структура не настроена</option>
                      ))}
                    </optgroup>
                  )}
                </>
              )
            })()}
          </select>
          {productGroupCode && !productGroups.find(g => g.code === productGroupCode)?.configured && (
            <p className="mt-1 text-xs text-amber-600">
              Структура кода для этой группы не настроена — сканирование КМ будет заблокировано.
            </p>
          )}
        </div>

        {!requiresKmFile && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-blue-800">
              КМ будут сканироваться в процессе работы. КА-коды генерируются или загружаются из файла.
            </p>
          </div>
        )}

        {isKmOnly && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm font-medium text-green-800">
              Загрузите файл с кодами маркировки — задание будет создано без коробок и паллет.
            </p>
          </div>
        )}

        {dupError && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">
                  Обнаружено {dupError.count} дублирующихся кодов
                </p>
                <a href={dupError.url} download className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-red-700 underline">
                  Скачать список дубликатов
                </a>
              </div>
            </div>
          </div>
        )}

        {requiresKmFile && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Файл с кодами маркировки <span className="text-red-500">*</span>
            </label>
            <input ref={fileRef} type="file" accept=".csv,.txt,.xml,.xlsx,.xls"
              className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
            <p className="text-xs text-gray-500 mt-1">CSV, TXT, XML, XLSX, XLS</p>
          </div>
        )}

        {!requiresKmFile && kaSourceMode === 'load' && (
          <div className="grid gap-4 grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Штук в коробке</label>
              <Input type="number" min="1" value={itemsPerBox} onChange={e => setItemsPerBox(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Коробок на палете</label>
              <Input type="number" min="1" value={boxesPerPallet} onChange={e => setBoxesPerPallet(e.target.value)} />
            </div>
          </div>
        )}

        {!isKmOnly && (
        <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                  kaSourceMode === 'generate' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setKaSourceMode('generate')}
              >
                <Package size={14} /> Сгенерировать КА
              </button>
              <button
                type="button"
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                  kaSourceMode === 'load' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setKaSourceMode('load')}
              >
                <Upload size={14} /> Загрузить КА
              </button>
            </div>

            {showKaGenerate && (
              <>
                {requiresKmFile && (
                  <div className={`grid gap-4 ${hasPalletLevel ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Штук в коробке</label>
                      <Input type="number" min="1" value={itemsPerBox} onChange={e => setItemsPerBox(e.target.value)} />
                    </div>
                    {hasPalletLevel && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Коробок на палете</label>
                        <Input type="number" min="1" value={boxesPerPallet} onChange={e => setBoxesPerPallet(e.target.value)} />
                      </div>
                    )}
                  </div>
                )}

                {!requiresKmFile && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">Композиция и объём генерации</p>
                    <div className="grid gap-4 grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Штук в коробке</label>
                        <Input type="number" min="1" value={itemsPerBox} onChange={e => setItemsPerBox(e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Коробок на палете</label>
                        <Input type="number" min="1" value={boxesPerPallet} onChange={e => setBoxesPerPallet(e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Количество паллет <span className="text-red-500">*</span>
                      </label>
                      <Input type="number" min="1" max="1000" value={palletCount} onChange={e => setPalletCount(e.target.value)} />
                      <p className="text-xs text-gray-500 mt-1">
                        Будет сгенерировано {parseInt(palletCount) || 0} паллет × {parseInt(boxesPerPallet) || 0} коробок = {(parseInt(palletCount) || 0) * (parseInt(boxesPerPallet) || 0)} коробок
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <p className="text-xs text-gray-500">
                    Выберите шаблоны КА. Структура AI и FNC1-позиции будут взяты из шаблона.{' '}
                    <Link to="/admin/ka-templates" className="text-blue-600 hover:underline">Управление шаблонами →</Link>
                  </p>
                  <div className={`grid gap-3 ${hasPalletLevel ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Шаблон коробки</label>
                      <select
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                        value={kaBoxTemplateId}
                        onChange={e => setKaBoxTemplateId(e.target.value)}
                      >
                        <option value="">— Не выбран —</option>
                        {boxTemplates.filter(t => t.is_active).map(t => (
                          <option key={t.template_id} value={t.template_id}>
                            {t.template_name}{t.is_default ? ' (по умолчанию)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {hasPalletLevel && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Шаблон паллеты</label>
                        <select
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                          value={kaPalletTemplateId}
                          onChange={e => setKaPalletTemplateId(e.target.value)}
                        >
                          <option value="">— Не выбран —</option>
                          {palletTemplates.filter(t => t.is_active).map(t => (
                            <option key={t.template_id} value={t.template_id}>
                              {t.template_name}{t.is_default ? ' (по умолчанию)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {(kaBoxTemplateId || kaPalletTemplateId) && (
                    <div className="text-xs text-gray-500 space-y-1 pt-1">
                      {kaBoxTemplateId && (() => {
                        const t = boxTemplates.find(x => x.template_id === kaBoxTemplateId)
                        return t ? (
                          <p>
                            <span className="font-medium text-amber-600">Коробки:</span>{' '}
                            {(t.ai_list ?? []).map(a => `(${a.ai})`).join(' ')}
                            {(t.fnc1_positions ?? []).length > 0 && ` · FNC1: ${t.fnc1_positions.join(', ')}`}
                            {` · Расш. цифра: ${t.extension_digit ?? '0'}`}
                          </p>
                        ) : null
                      })()}
                      {hasPalletLevel && kaPalletTemplateId && (() => {
                        const t = palletTemplates.find(x => x.template_id === kaPalletTemplateId)
                        return t ? (
                          <p>
                            <span className="font-medium text-purple-600">Паллеты:</span>{' '}
                            {(t.ai_list ?? []).map(a => `(${a.ai})`).join(' ')}
                            {(t.fnc1_positions ?? []).length > 0 && ` · FNC1: ${t.fnc1_positions.join(', ')}`}
                            {` · Расш. цифра: ${t.extension_digit ?? '1'}`}
                          </p>
                        ) : null
                      })()}
                    </div>
                  )}
                </div>

                {(autoAIFields.length > 0 || manualAIFields.length > 0) && (
                  <div className="border-t border-gray-100 pt-3 space-y-3">
                    <p className="text-sm font-medium text-gray-700">Данные для КА кодов</p>

                    {autoAIFields.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {autoAIFields.map((entry) => (
                          <span
                            key={entry.ai}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-xs border border-blue-100"
                          >
                            <span className="font-mono">({entry.ai})</span>
                            <span>{entry.label}</span>
                            <span className="text-blue-600 font-medium">{autoSourceLabel(entry.source)}</span>
                          </span>
                        ))}
                      </div>
                    )}

                    {manualAIFields.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-600">Ручные AI значения</p>
                        {manualAIFields.map((entry) => (
                          <div key={entry.ai} className="flex items-center gap-2">
                            <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 shrink-0">
                              ({entry.ai})
                            </span>
                            <span className="text-xs text-gray-600 shrink-0 w-40 truncate">
                              {entry.label}
                              {entry.required && <span className="text-red-500 ml-0.5">*</span>}
                            </span>
                            <Input
                              className="flex-1 text-sm"
                              type={entry.ai === '11' || entry.ai === '17' ? 'date' : 'text'}
                              value={manualAiValues[entry.ai] ?? entry.default_value ?? ''}
                              onChange={e => setManualAiValues(prev => ({ ...prev, [entry.ai]: e.target.value }))}
                              placeholder={aiInputHint(entry.ai) || entry.default_value || 'Значение AI'}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {kaSourceMode === 'load' && (
              <>
                {requiresKmFile && (
                  <p className="text-xs text-gray-500">
                    Количество штук в коробке и коробок на палете будет определено автоматически из загруженных файлов КА.
                  </p>
                )}
                <FileImportInline
                  needsTwoFiles={hasPalletLevel}
                  codeType="ka_box_pallet"
                  onChange={setKaFileData}
                />
              </>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
            <Upload className="w-4 h-4 mr-1" /> Создать задание
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/tasks')}>Отмена</Button>
        </div>
      </form>
    </div>
  )
}
